import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMap } from 'react-leaflet';
import { setBaseLayer, setDataLayer, setOverlayLayer } from '../../store/storeSlice';
import Control from 'react-leaflet-custom-control';
import {
    Button,
    Box,
    Switch,
    Typography,
    Divider,
    Chip,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import TrafficIcon from '@mui/icons-material/Traffic';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import ScaleIcon from '@mui/icons-material/Scale';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import Brightness6Icon from '@mui/icons-material/Brightness6';
import ContrastIcon from '@mui/icons-material/Contrast';
import PaletteIcon from '@mui/icons-material/Palette';
import OpacityIcon from '@mui/icons-material/Opacity';
import RefreshIcon from '@mui/icons-material/Refresh';
import { setBaseLayer as setBaseLayerRedux, setOverlayLayer as setOverlayLayerRedux, setDataLayer as setDataLayerRedux, setMapSettings } from '../../store/storeSlice';
import { createPortal } from 'react-dom';

const LayersControl = ({ position = 'topright', notify }) => {
    const map = useMap();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const objects = useSelector(state => state.store.objects);
    const buttonRef = useRef(null);
    const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });


    const { base: reduxBaseLayer, overlay: reduxOverlayLayers, data: reduxDataLayers, settings: reduxSettings } = useSelector(state => state.store.layers)
    const [baseLayer, setBaseLayer] = useState(reduxBaseLayer || 1)
    const [overlayLayers, setOverlayLayers] = useState(reduxOverlayLayers || '{}')
    const [dataLayers, setDataLayers] = useState(reduxDataLayers || {})
    const [settings, setSettings] = useState(reduxSettings || { brightness: 100, contrast: 100, saturation: 100, opacity: 100 })
    // Состояние базовых слоев
    // const [baseLayer, setBaseLayer] = useState(parseInt(localStorage.getItem('base_layer') || '1'));

    // Состояние overlay слоев

    const handleToggle = () => {
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPanelPosition({
                top: rect.top,
                right: window.innerWidth - rect.left + 8 // 8px отступ от кнопки
            });
        }
        setOpen(!open);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Закрытие панели при клике вне её
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            const panel = document.querySelector('[data-layers-panel]');
            const button = event.target.closest('button[aria-label], button');

            // Не закрываем, если клик внутри панели или на кнопке открытия
            if (panel && panel.contains(event.target)) return;
            if (button && button.closest('.leaflet-control')) return;

            handleClose();
        };

        // Небольшая задержка, чтобы не закрыть сразу после открытия
        const timer = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
            map.getContainer().addEventListener('click', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handleClickOutside);
            map.getContainer().removeEventListener('click', handleClickOutside);
        };
    }, [open, map]);

    const baseLayers = [
        { id: 1, name: 'OSM' },
        { id: 2, name: '2GIS' },
        { id: 3, name: 'Яндекс' },
        { id: 4, name: 'Спутник(Я)' },
        { id: 5, name: 'Гибрид(Я)' },
        { id: 6, name: 'Google' },
        { id: 7, name: 'Спутник(G)' },
        { id: 8, name: 'Гибрид(G)' },
        { id: 9, name: 'ArcGis' }
    ];

    const overlayLayersConfig = useMemo(() => {
        const main = [
            {
                key: 'crossroad',
                name: 'Перекрестки',
                icon: <TrafficIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.crossroad?.length || 0
            },
            {
                key: 'speed',
                name: 'Скоростные',
                icon: <SpeedIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.speed?.length || 0
            },
            {
                key: 'parking',
                name: 'Парковка',
                icon: <LocalParkingIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.parking?.length || 0
            },
            {
                key: 'vgk',
                name: 'Весогабарит',
                icon: <ScaleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.vgk?.length || 0
            },
            {
                key: 'deact',
                name: 'Деактивированные',
                icon: <PowerOffIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.deactivated?.length || 0
            },
            {
                key: 'vehicles',
                name: 'Транспорт',
                icon: <DirectionsCarIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
                count: objects?.vehicles?.length || 0
            }
        ]
        const others = Object.keys(objects?.others || {}).map((key) => (
            {
                key: key,
                name: key,
                icon: null,
                count: objects?.others[key]?.length || 0
            }
        ))
        return { main, others }
    }, [objects]);

    const dataLayersConfig = [
        {
            key: 'showCameraDirection',
            name: 'Направления камер',
            hide: false,
            icon: null,
            count: null
        }, {
            key: 'showSpeedLimit',
            name: 'Ограничение скорости',
            hide: false,
            icon: null,
            count: null
        },

    ]

    useEffect(() => {
        map.on('baselayerchange', (e) => {
            console.log('baselayerchange', e)
            setTimeout(() => dispatch(setBaseLayerRedux(e.id)), 100)
        })
        map.on('overlaylayerchange', (e) => {
            console.log('overlaylayerchange', e)
            setTimeout(() => dispatch(setOverlayLayerRedux({ name: e.name, state: e.state })), 100)
        })
        map.on('datalayerchange', (e) => {
            console.log('datalayerchange', e)
            setTimeout(() => dispatch(setDataLayerRedux(e)), 100)
        })
    }, [])

    const handleBaseLayerChange = (layerId) => {
        setBaseLayer(layerId);
        map.fire('baselayerchange', { id: layerId });
        // Применяем фильтры с задержкой, чтобы новый слой успел отрисоваться
        setTimeout(() => applyFilters(settings), 300);
        setTimeout(() => applyFilters(settings), 1000);
    };

    const handleOverlayLayerChange = (layerKey, group = 'main') => {
        console.log(layerKey, group)
        let currentState;
        let newState;

        if (group === 'others') {
            currentState = Boolean(overlayLayers.others?.[layerKey]);
            newState = !currentState;
            setOverlayLayers(prev => ({
                ...prev,
                others: { ...prev.others, [layerKey]: newState }
            }));
        } else {
            currentState = Boolean(overlayLayers[layerKey]);
            newState = !currentState;
            setOverlayLayers(prev => ({ ...prev, [layerKey]: newState }));
        }

        dispatch(setOverlayLayer({
            name: layerKey,
            state: newState,
            group: group
        }));
        map.fire('overlaylayerchange', { name: layerKey, state: newState, group: group });
    };

    const handleDataLayerChange = (layerKey) => {
        const currentState = Boolean(dataLayers[layerKey]);
        const newState = !currentState;
        setDataLayers(prev => ({ ...prev, [layerKey]: newState }));
        map.fire('datalayerchange', { name: layerKey, state: newState });
    };

    const applyFilters = (newSettings) => {
        const { brightness, contrast, saturation, opacity } = newSettings || { brightness: 100, contrast: 100, saturation: 100, opacity: 100 };
        const filterValue = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) opacity(${opacity}%)`;
        // Ищем YandexMutantLayer контейнер
        const yandexContainer = map.getContainer().querySelector('.leaflet-yandex-mutant');
        if (yandexContainer) {
            yandexContainer.style.filter = filterValue;
            return;
        }
        const tilePane = map.getContainer().querySelector('.leaflet-tile-pane');
        if (tilePane) {
            tilePane.style.filter = filterValue;
            return;
        }
    };

    const handleSettingChange = (key, value) => {
        console.log(key, value)
        const newSettings = { ...settings, [key]: value };
        applyFilters(newSettings);
        setSettings(newSettings);
        setTimeout(() => dispatch(setMapSettings(newSettings)), 500);
    };

    const resetSettings = () => {
        const defaultSettings = { brightness: 100, contrast: 100, saturation: 100, opacity: 100 };
        applyFilters(defaultSettings);
        setSettings(defaultSettings);
        dispatch(setMapSettings(defaultSettings));
    };

    // Применяем фильтры при загрузке и при смене слоя
    useEffect(() => {
        if (settings) {
            applyFilters(settings);
        }
    }, []);

    // Применяем фильтры при смене базового слоя
    useEffect(() => {
        if (settings) {
            // Задержка для ожидания отрисовки нового слоя
            const timer1 = setTimeout(() => applyFilters(settings), 300);
            const timer2 = setTimeout(() => applyFilters(settings), 1000);
            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
            };
        }
    }, [baseLayer]);


    return (
        <Control position={position}>
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '6px', md: '8px' },
                        boxShadow: 'var(--shadow-md)',
                        border: '1px solid var(--border-default)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                    }}
                >
                    <IconButton
                        ref={buttonRef}
                        onClick={handleToggle}
                        sx={{
                            padding: { xs: '6px', md: '8px' },
                            color: 'var(--text-primary)',
                            '&:hover': {
                                backgroundColor: 'var(--selected-bg)',
                            },
                        }}
                    >
                        <LayersIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
                    </IconButton>
                </Box>

                {createPortal(
                    <Box
                        data-layers-panel
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            position: 'fixed',
                            top: panelPosition.top,
                            right: panelPosition.right,
                            backgroundColor: 'var(--background-paper)',
                            border: '1px solid var(--border-default)',
                            boxShadow: 'var(--shadow-lg)',
                            borderRadius: { xs: '6px', md: '8px' },
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            width: { xs: '300px', md: '300px' },
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            zIndex: 10000,
                            // Анимация открытия/закрытия
                            opacity: open ? 1 : 0,
                            transform: open ? 'translateX(0) scale(1)' : 'translateX(10px) scale(0.95)',
                            visibility: open ? 'visible' : 'hidden',
                            pointerEvents: open ? 'auto' : 'none',
                            transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out, visibility 0.2s ease-in-out',
                        }}
                    >
                        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                            <Typography variant="caption" sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                color: 'var(--text-secondary)'
                            }}>
                                <MapIcon sx={{ fontSize: { xs: 16, md: 18 }, mr: 0.5, color: 'var(--primary-color)' }} />
                                Базовые слои
                            </Typography>
                            {/* Базовые слои */}
                            <Box sx={{ mb: 1, mt: 1, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 0.4, md: 0.5 } }}>
                                {baseLayers.map((layer) => (
                                    <Button
                                        key={layer.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBaseLayerChange(layer.id);
                                        }}
                                        variant={baseLayer === layer.id ? 'contained' : 'outlined'}
                                        size="small"
                                        sx={{
                                            mb: 0.2,
                                            textTransform: 'none',
                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                            width: 'auto',
                                            minWidth: 'auto',
                                            py: { xs: 0.15, md: 0.2 },
                                            px: { xs: 0.5, md: 0.75 },
                                            borderColor: 'var(--border-default)',
                                            color: baseLayer === layer.id
                                                ? '#ffffff'
                                                : 'var(--text-primary)',
                                            backgroundColor: baseLayer === layer.id
                                                ? 'var(--primary-color)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: baseLayer === layer.id
                                                    ? 'var(--primary-dark)'
                                                    : 'var(--selected-bg)',
                                                borderColor: 'var(--border-default)'
                                            }
                                        }}
                                    >
                                        {layer.name}
                                    </Button>
                                ))}
                            </Box>

                            <Divider sx={{ my: 1, borderColor: 'var(--divider-color)' }} />

                            {/* Overlay слои */}
                            <Typography variant="caption" sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                color: 'var(--text-secondary)'
                            }}>
                                <LayersIcon sx={{ fontSize: { xs: 12, md: 14 }, mr: 0.5, color: 'var(--primary-color)' }} />
                                Слои объектов
                            </Typography>

                            {overlayLayersConfig.main.map((layer) => (
                                <Box key={layer.key} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: { xs: 0.1, md: 0.25 },
                                    minHeight: { xs: '20px', md: '26px' }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 }, flex: 1 }}>
                                        <Box sx={{ color: 'var(--primary-color)' }}>
                                            {layer.icon}
                                        </Box>
                                        <Typography variant="body2" sx={{
                                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                                            color: 'var(--text-primary)'
                                        }}>
                                            {layer.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <Chip
                                            label={layer.count}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                height: { xs: '16px', md: '18px' },
                                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                minWidth: { xs: '20px', md: '24px' },
                                                borderColor: 'var(--border-default)',
                                                color: 'var(--text-secondary)',
                                                backgroundColor: 'var(--info-bg)',
                                                '& .MuiChip-label': {
                                                    px: { xs: 0.4, md: 0.5 }
                                                }
                                            }}
                                        />
                                        <Switch
                                            checked={overlayLayers[layer.key]}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleOverlayLayerChange(layer.key);
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase': {
                                                    padding: { xs: '6px', md: '5px' },
                                                    '&.Mui-checked': {
                                                        color: 'var(--primary-color)',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: 'var(--primary-color)',
                                                        },
                                                    },
                                                },
                                                '& .MuiSwitch-thumb': {
                                                    width: { xs: 12, md: 14 },
                                                    height: { xs: 12, md: 14 },
                                                },
                                                '& .MuiSwitch-track': {
                                                    borderRadius: 10,
                                                    backgroundColor: 'var(--border-muted)',
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>


                            ))}

                            {overlayLayersConfig.others && overlayLayersConfig.others.length > 0 && (
                                <Accordion
                                    defaultExpanded={false}
                                    sx={{
                                        mt: 1,
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                        '&:before': {
                                            display: 'none'
                                        },
                                        '&.Mui-expanded': {
                                            margin: '8px 0'
                                        }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <ExpandMoreIcon sx={{
                                                color: 'var(--primary-color)',
                                                fontSize: { xs: 16, md: 18 }
                                            }} />
                                        }
                                        sx={{
                                            minHeight: { xs: '32px', md: '36px' },
                                            py: 0,
                                            '&.Mui-expanded': {
                                                minHeight: { xs: '32px', md: '36px' }
                                            },
                                            '& .MuiAccordionSummary-content': {
                                                margin: { xs: '6px 0', md: '8px 0' },
                                                '&.Mui-expanded': {
                                                    margin: { xs: '6px 0', md: '8px 0' }
                                                }
                                            }
                                        }}
                                    >
                                        <Typography variant="body2" sx={{
                                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                                            fontWeight: 500,
                                            color: 'var(--text-primary)'
                                        }}>
                                            Другие группы
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0, pb: { xs: 0.4, md: 0.5 }, px: 0 }}>
                                        {overlayLayersConfig.others.map((layer) => (
                                            <Box key={layer.key} sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: { xs: 0.2, md: 0.25 },
                                                minHeight: { xs: '28px', md: '32px' },
                                                pl: { xs: 0.4, md: 0.5 }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 }, flex: 1 }}>
                                                    {layer.icon && (
                                                        <Box sx={{ color: 'var(--primary-color)' }}>
                                                            {layer.icon}
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" sx={{
                                                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                                                        color: 'var(--text-primary)'
                                                    }}>
                                                        {layer.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                                    <Chip
                                                        label={layer.count}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            height: { xs: '16px', md: '18px' },
                                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                            minWidth: { xs: '20px', md: '24px' },
                                                            borderColor: 'var(--border-default)',
                                                            color: 'var(--text-secondary)',
                                                            backgroundColor: 'var(--info-bg)',
                                                            '& .MuiChip-label': {
                                                                px: { xs: 0.4, md: 0.5 }
                                                            }
                                                        }}
                                                    />
                                                    <Switch
                                                        checked={Boolean(overlayLayers.others?.[layer.key])}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleOverlayLayerChange(layer.key, 'others');
                                                        }}
                                                        size="small"
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase': {
                                                                padding: { xs: '6px', md: '5px' },
                                                                '&.Mui-checked': {
                                                                    color: 'var(--primary-color)',
                                                                    '& + .MuiSwitch-track': {
                                                                        backgroundColor: 'var(--primary-color)',
                                                                    },
                                                                },
                                                            },
                                                            '& .MuiSwitch-thumb': {
                                                                width: { xs: 12, md: 14 },
                                                                height: { xs: 12, md: 14 },
                                                            },
                                                            '& .MuiSwitch-track': {
                                                                borderRadius: 10,
                                                                backgroundColor: 'var(--border-muted)',
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            <Divider sx={{ my: 1, borderColor: 'var(--divider-color)' }} />

                            {/* Слои данных */}
                            <Typography variant="caption" sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                color: 'var(--text-secondary)'
                            }}>
                                <LayersIcon sx={{ fontSize: { xs: 12, md: 14 }, mr: 0.5, color: 'var(--primary-color)' }} />
                                Слои данных
                            </Typography>

                            {dataLayersConfig.filter(dl => !dl.hide).map((layer) => (
                                <Box key={layer.key} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: { xs: 0.1, md: 0.25 },
                                    minHeight: { xs: '20px', md: '26px' }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 }, flex: 1 }}>
                                        <Box sx={{ color: 'var(--primary-color)' }}>
                                            {layer.icon}
                                        </Box>
                                        <Typography variant="body2" sx={{
                                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                                            color: 'var(--text-primary)'
                                        }}>
                                            {layer.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <Switch
                                            checked={dataLayers[layer.key]}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleDataLayerChange(layer.key);
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase': {
                                                    padding: { xs: '6px', md: '5px' },
                                                    '&.Mui-checked': {
                                                        color: 'var(--primary-color)',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: 'var(--primary-color)',
                                                        },
                                                    },
                                                },
                                                '& .MuiSwitch-thumb': {
                                                    width: { xs: 12, md: 14 },
                                                    height: { xs: 12, md: 14 },
                                                },
                                                '& .MuiSwitch-track': {
                                                    borderRadius: 10,
                                                    backgroundColor: 'var(--border-muted)',
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>


                            ))}

                            <Divider sx={{ my: 1, borderColor: 'var(--divider-color)' }} />

                            {/* Настройки отображения */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 0.4, md: 0.5 } }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                    color: 'var(--text-secondary)'
                                }}>
                                    <SettingsIcon sx={{ fontSize: { xs: 12, md: 14 }, mr: 0.5, color: 'var(--primary-color)' }} />
                                    Настройки отображения
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={resetSettings}
                                    sx={{
                                        p: { xs: 0.4, md: 0.5 },
                                        color: 'var(--primary-color)',
                                        '&:hover': {
                                            backgroundColor: 'var(--selected-bg)',
                                        }
                                    }}
                                    title="Сбросить настройки"
                                >
                                    <RefreshIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                </IconButton>
                            </Box>

                            {/* Яркость */}
                            <Box sx={{ mb: { xs: 0.1, md: 0.2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <Brightness6Icon sx={{ fontSize: { xs: 12, md: 14 }, color: 'var(--primary-color)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                                            Яркость
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        fontWeight: 600,
                                        minWidth: { xs: '30px', md: '35px' },
                                        textAlign: 'right'
                                    }}>
                                        {settings.brightness}%
                                    </Typography>
                                </Box>
                                <Slider
                                    value={settings.brightness}
                                    onChange={(e, value) => {
                                        console.log(value)
                                        // e.stopPropagation()
                                        handleSettingChange('brightness', value)
                                    }}
                                    // onClick={(e) => console.log(e)}
                                    min={0}
                                    max={200}
                                    step={1}
                                    size="small"
                                    sx={{
                                        padding: '10px 0 !important',
                                        color: 'var(--primary-color)',
                                        height: { xs: 3, md: 4 },
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 12, md: 14 },
                                            height: { xs: 12, md: 14 },
                                            '&:hover': {
                                                boxShadow: '0 0 0 8px var(--selected-bg)',
                                            },
                                        },
                                        '& .MuiSlider-track': { height: { xs: 3, md: 4 } },
                                        '& .MuiSlider-rail': { height: { xs: 3, md: 4 }, opacity: 0.3 },
                                    }}
                                />
                            </Box>

                            {/* Контрастность */}
                            <Box sx={{ mb: { xs: 0.1, md: 0.2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <ContrastIcon sx={{ fontSize: { xs: 12, md: 14 }, color: 'var(--primary-color)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                                            Контрастность
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        fontWeight: 600,
                                        minWidth: { xs: '30px', md: '35px' },
                                        textAlign: 'right'
                                    }}>
                                        {settings.contrast}%
                                    </Typography>
                                </Box>
                                <Slider
                                    value={settings.contrast}
                                    onChange={(e, value) => handleSettingChange('contrast', value)}
                                    min={0}
                                    max={200}
                                    step={1}
                                    size="small"
                                    sx={{
                                        padding: '10px 0 !important',
                                        color: 'var(--primary-color)',
                                        height: { xs: 3, md: 4 },
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 12, md: 14 },
                                            height: { xs: 12, md: 14 },
                                            '&:hover': {
                                                boxShadow: '0 0 0 8px var(--selected-bg)',
                                            },
                                        },
                                        '& .MuiSlider-track': { height: { xs: 3, md: 4 } },
                                        '& .MuiSlider-rail': { height: { xs: 3, md: 4 }, opacity: 0.3 },
                                    }}
                                />
                            </Box>

                            {/* Насыщенность */}
                            <Box sx={{ mb: { xs: 0.1, md: 0.2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <PaletteIcon sx={{ fontSize: { xs: 12, md: 14 }, color: 'var(--primary-color)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                                            Насыщенность
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        fontWeight: 600,
                                        minWidth: { xs: '30px', md: '35px' },
                                        textAlign: 'right'
                                    }}>
                                        {settings.saturation}%
                                    </Typography>
                                </Box>
                                <Slider
                                    value={settings.saturation}
                                    onChange={(e, value) => handleSettingChange('saturation', value)}
                                    min={0}
                                    max={200}
                                    step={1}
                                    size="small"
                                    sx={{
                                        padding: '10px 0 !important',
                                        color: 'var(--primary-color)',
                                        height: { xs: 3, md: 4 },
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 12, md: 14 },
                                            height: { xs: 12, md: 14 },
                                            '&:hover': {
                                                boxShadow: '0 0 0 8px var(--selected-bg)',
                                            },
                                        },
                                        '& .MuiSlider-track': { height: { xs: 3, md: 4 } },
                                        '& .MuiSlider-rail': { height: { xs: 3, md: 4 }, opacity: 0.3 },
                                    }}
                                />
                            </Box>

                            {/* Прозрачность */}
                            <Box sx={{ mb: { xs: 0.1, md: 0.2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                                        <OpacityIcon sx={{ fontSize: { xs: 12, md: 14 }, color: 'var(--primary-color)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                                            Прозрачность
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        fontWeight: 600,
                                        minWidth: { xs: '30px', md: '35px' },
                                        textAlign: 'right'
                                    }}>
                                        {100 - settings.opacity}%
                                    </Typography>
                                </Box>
                                <Slider
                                    value={100 - settings.opacity}
                                    onChange={(e, value) => handleSettingChange('opacity', 100 - value)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    size="small"
                                    sx={{
                                        padding: '10px 0 !important',
                                        color: 'var(--primary-color)',
                                        height: { xs: 3, md: 4 },
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 12, md: 14 },
                                            height: { xs: 12, md: 14 },
                                            '&:hover': {
                                                boxShadow: '0 0 0 8px var(--selected-bg)',
                                            },
                                        },
                                        '& .MuiSlider-track': { height: { xs: 3, md: 4 } },
                                        '& .MuiSlider-rail': { height: { xs: 3, md: 4 }, opacity: 0.3 },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    , document.body)}
            </Box>
        </Control>
    );
};

export default LayersControl; 